import React, { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { NavigationContext } from "../../context/navigation-context";
import SearchFilter from "../../components/Filter/SearchFilter";
import NoResults from "../../components/NoResults/NoResults";
import useFetch from "../../hooks/fetchMSSQL-hook";

const AdvancedWIS = () => {
  const { setHeaderData } = useContext(NavigationContext);
  const { fetchMssqlApi } = useFetch();

  const [data, setData] = useState([]);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const defaultAdditionalFilter = {
    visit: "",
    user: "",
    machine: "",
    salesValue: "",
    salesQuantity: "",
    stateAndCapacity: "",
    loadQuantity: "",
    correctionName: "",
    comment: "",
  };

  const [additionalFilter, setAdditionalFilter] = useState(
    defaultAdditionalFilter
  );

  const defaultFilter = {
    name: "advancedWis",
    page: 1,
    rowsPerPage: 100,
    sortBy: "CreateDateTime|DESC",
    dateTimeRangeFrom: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    dateTimeRangeTo: moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    machineId: null,
    machineName: "",
    machineTags: { id: [], name: [] },
    userId: null,
    userName: "",
  };

  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 100,
  });

  useEffect(() => {
    setHeaderData({ text: "Rozszerzony widok wizyt" });
  }, [setHeaderData]);

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getMachineIdsByTags = (machineTagIds) =>
    new Promise((resolve, reject) => {
      fetchMssqlApi(
        "tags/getMachineIdsByTags",
        {
          method: "POST",
          hideNotification: true,
          data: { machineTagIds },
        },
        (response) => {
          resolve(
            JSON.stringify((response || []).map(Number).filter(Number.isFinite))
          );
        },
        reject
      );
    });

  const fetchData = async (nextFilter, pageSize, page) => {
    const currentFilter = nextFilter || filter;
    const rowsPerPage =
      pageSize ?? settingsFilter?.rowsPerPage ?? currentFilter.rowsPerPage ?? 100;
    const pageNo = Math.max(1, Number(page ?? currentFilter.page) || 1);

    let machineIds = [];

    if (currentFilter?.machineTags?.id?.length) {
      const idsByTags = await getMachineIdsByTags(
        currentFilter.machineTags.id
      );
      machineIds = JSON.parse(idsByTags || "[]");
    }

    if (currentFilter?.machineId) {
      machineIds.push(Number(currentFilter.machineId));
    }

    machineIds = [...new Set(machineIds.filter(Number.isFinite))];

    fetchMssqlApi(
      "advanced-wis",
      {
        method: "POST",
        hideNotification: true,
        data: {
          fromCreateDateTime: currentFilter.dateTimeRangeFrom,
          toCreateDateTime: currentFilter.dateTimeRangeTo,
          machineId: JSON.stringify(machineIds),
          createUserId: currentFilter.userId,
          page: pageNo,
          rowsPerPage,
          sortBy: currentFilter.sortBy,
        },
      },
      (response) => setData(response || [])
    );
  };

  const sortState = useMemo(() => {
    const [field, direction] = String(filter.sortBy || "").split("|");
    return { field, direction: String(direction || "").toUpperCase() };
  }, [filter.sortBy]);

  const arrow = (field) => {
    if (sortState.field !== field) return "";
    return sortState.direction === "ASC" ? " ▲" : " ▼";
  };

  const sortDefaults = {
    CreateDateTime: "DESC",
    CreatedBy: "ASC",
    MachineFullName: "ASC",
    SalesValue: "DESC",
    SalesQuantity: "DESC",
    StateQuantity: "DESC",
    LoadQuantity: "DESC",
    CorrectionName: "ASC",
    Comment: "ASC",
  };

  const handleHeaderSort = (field) => {
    const [currentField, currentDirectionRaw] = String(
      filter.sortBy || ""
    ).split("|");
    const currentDirection = String(currentDirectionRaw || "").toUpperCase();

    const nextDirection =
      currentField === field
        ? currentDirection === "ASC"
          ? "DESC"
          : "ASC"
        : sortDefaults[field] || "ASC";

    const nextFilter = {
      ...filter,
      sortBy: `${field}|${nextDirection}`,
      page: 1,
    };

    setFilter(nextFilter);
    fetchData(nextFilter, settingsFilter?.rowsPerPage, 1);
  };

  const includesFilter = (value, query) =>
    !query ||
    String(value ?? "")
      .toLocaleLowerCase("pl")
      .includes(String(query).toLocaleLowerCase("pl"));

  const filteredData = useMemo(
    () =>
      data.filter(
        (entry) =>
          includesFilter(entry.VisitCreated, additionalFilter.visit) &&
          includesFilter(entry.CreatedBy, additionalFilter.user) &&
          includesFilter(entry.MachineFullName, additionalFilter.machine) &&
          includesFilter(entry.SalesValue, additionalFilter.salesValue) &&
          includesFilter(
            entry.SalesQuantity,
            additionalFilter.salesQuantity
          ) &&
          includesFilter(
            entry.StateAndCapacity,
            additionalFilter.stateAndCapacity
          ) &&
          includesFilter(entry.LoadQuantity, additionalFilter.loadQuantity) &&
          includesFilter(
            entry.CorrectionName,
            additionalFilter.correctionName
          ) &&
          includesFilter(entry.Comment, additionalFilter.comment)
      ),
    [data, additionalFilter]
  );

  const setColumnFilter = (name, value) => {
    setAdditionalFilter((previous) => ({ ...previous, [name]: value }));
  };

  const headerInput = (name, ariaLabel) => (
    <input
      type="text"
      aria-label={ariaLabel}
      value={additionalFilter[name] || ""}
      onChange={({ target: { value } }) => setColumnFilter(name, value)}
      className="form-control form-control-sm mt-1"
      style={{ minWidth: "85px", height: "24px", fontSize: "11px" }}
    />
  );

  const formatDecimal = (value) => {
    if (value == null || value === "") return "0";

    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? numericValue.toLocaleString("pl-PL", { maximumFractionDigits: 2 })
      : String(value).replace(".", ",");
  };

  return (
    <div className="d-flex flex-column h-100">
      <SearchFilter
        filterName="advancedWis"
        defaultFilter={defaultFilter}
        onFilterChange={(nextFilter) =>
          setFilter((previous) => ({ ...previous, ...nextFilter }))
        }
        onSettingsChange={(settings) =>
          setSettingsFilter((previous) => ({ ...previous, ...settings }))
        }
        search={fetchData}
        totalCount={data.length}
        dataList={data}
        defaultAdditionalFilter={defaultAdditionalFilter}
        additionalFilter={additionalFilter}
        onAdditionalFilterChange={setAdditionalFilter}
        forNow
      />

      <div
        className="border overflow-auto custom-scroll flex-grow-1"
        style={{ maxHeight: `${Math.max(windowHeight - 146, 240)}px` }}
      >
        {filteredData.length ? (
          <table
            className="table table-sm table-striped table-hover mb-0"
            style={{ fontSize: "13px" }}
          >
            <colgroup>
              <col style={{ minWidth: "150px", width: "150px" }} />
              <col style={{ minWidth: "165px", width: "165px" }} />
              <col style={{ minWidth: "310px" }} />
              <col style={{ minWidth: "120px", width: "120px" }} />
              <col style={{ minWidth: "125px", width: "125px" }} />
              <col style={{ minWidth: "175px", width: "175px" }} />
              <col style={{ minWidth: "125px", width: "125px" }} />
              <col style={{ minWidth: "160px", width: "160px" }} />
              <col style={{ minWidth: "210px" }} />
            </colgroup>

            <thead
              className="headers-color"
              style={{ position: "sticky", top: 0, zIndex: 2 }}
            >
              <tr className="text-center align-middle">
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("CreateDateTime")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("CreateDateTime")
                    }
                  >
                    Wizyta{arrow("CreateDateTime")}
                  </span>
                  {headerInput("visit", "Filtr wizyty")}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("CreatedBy")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("CreatedBy")
                    }
                  >
                    Serwisant{arrow("CreatedBy")}
                  </span>
                  {headerInput("user", "Filtr serwisanta")}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("MachineFullName")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("MachineFullName")
                    }
                  >
                    Maszyna{arrow("MachineFullName")}
                  </span>
                  {headerInput("machine", "Filtr maszyny")}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("SalesValue")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("SalesValue")
                    }
                  >
                    Sprzedaż{arrow("SalesValue")}
                  </span>
                  {headerInput("salesValue", "Filtr wartości sprzedaży")}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("SalesQuantity")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("SalesQuantity")
                    }
                  >
                    Sprzedaż (Sztuk){arrow("SalesQuantity")}
                  </span>
                  {headerInput("salesQuantity", "Filtr liczby sprzedanych sztuk")}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("StateQuantity")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("StateQuantity")
                    }
                  >
                    Pozostawiono/Pojemność{arrow("StateQuantity")}
                  </span>
                  {headerInput(
                    "stateAndCapacity",
                    "Filtr pozostawionej ilości i pojemności"
                  )}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("LoadQuantity")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("LoadQuantity")
                    }
                  >
                    Doładowano{arrow("LoadQuantity")}
                  </span>
                  {headerInput("loadQuantity", "Filtr doładowania")}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("CorrectionName")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("CorrectionName")
                    }
                  >
                    Korekty{arrow("CorrectionName")}
                  </span>
                  {headerInput("correctionName", "Filtr korekty")}
                </th>
                <th>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleHeaderSort("Comment")}
                    onKeyDown={({ key }) =>
                      key === "Enter" && handleHeaderSort("Comment")
                    }
                  >
                    Komentarz{arrow("Comment")}
                  </span>
                  {headerInput("comment", "Filtr komentarza")}
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((entry) => (
                <tr
                  key={entry.VisitId}
                  className="align-middle"
                  style={{ height: "33px" }}
                >
                  <td className="text-nowrap">{entry.VisitCreated}</td>
                  <td>{entry.CreatedBy}</td>
                  <td>{entry.MachineFullName}</td>
                  <td className="text-end">{formatDecimal(entry.SalesValue)}</td>
                  <td className="text-end">{entry.SalesQuantity ?? 0}</td>
                  <td className="text-center">{entry.StateAndCapacity}</td>
                  <td className="text-end">{entry.LoadQuantity ?? 0}</td>
                  <td>{entry.CorrectionName}</td>
                  <td>{entry.Comment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <NoResults />
        )}
      </div>
    </div>
  );
};

export default AdvancedWIS;